import { createContext, useContext, useLayoutEffect, useState, ReactNode, useCallback } from 'react';

type ThemePreference = 'light' | 'dark' | 'system';
type ResolvedTheme = 'light' | 'dark';

interface ThemeContextType {
  theme: ThemePreference;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Helper to get resolved theme from preference
function getResolvedTheme(preference: ThemePreference): ResolvedTheme {
  if (preference === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return preference;
}

// Helper to apply theme class to document - updates both class and data attribute
function applyThemeToDOM(resolved: ResolvedTheme) {
  const root = document.documentElement;

  // Remove both possible classes first, then add the correct one
  root.classList.remove('dark', 'light');
  root.classList.add(resolved);
  root.setAttribute('data-theme', resolved);

  // Also set color-scheme for native elements (scrollbars, form controls)
  root.style.colorScheme = resolved;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemePreference>(() => {
    if (typeof window === 'undefined') return 'system';
    const saved = localStorage.getItem('theme') as ThemePreference | null;
    return saved || 'system';
  });

  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => {
    if (typeof window === 'undefined') return 'light';
    const saved = localStorage.getItem('theme') as ThemePreference | null;
    return getResolvedTheme(saved || 'system');
  });

  // Apply theme to DOM synchronously before paint
  useLayoutEffect(() => {
    const newResolved = getResolvedTheme(theme);
    setResolvedTheme(newResolved);
    applyThemeToDOM(newResolved);
  }, [theme]);

  // Listen to system preference changes
  useLayoutEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = () => {
      if (theme === 'system') {
        const newResolved = getResolvedTheme('system');
        setResolvedTheme(newResolved);
        applyThemeToDOM(newResolved);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  const setTheme = useCallback((newTheme: ThemePreference) => {
    // Update localStorage first
    localStorage.setItem('theme', newTheme);

    // Calculate and apply immediately
    const newResolved = getResolvedTheme(newTheme);
    applyThemeToDOM(newResolved);

    // Then update React state
    setThemeState(newTheme);
    setResolvedTheme(newResolved);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
